import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, apiError } from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import { useSchool } from "./SchoolSite";
import { PubField } from "./AdmissionSteps";
import { ArrowLeft, ShieldCheck, Loader2, IndianRupee, Receipt, Smartphone } from "lucide-react";

const inr = (n) => "₹" + (n || 0).toLocaleString("en-IN");

export default function PayFees() {
  const { code } = useParams();
  const { data: s } = useSchool(code);
  const [adm, setAdm] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState({ sent: false, code: "", mock: "", token: "" });
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [amount, setAmount] = useState("");

  const sendOtp = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/otp/send`, { phone });
      setOtp({ ...otp, sent: true, mock: data.mock_otp, code: "" });
      toast.success("OTP sent", { description: data.mock ? `MOCK SMS — OTP ${data.mock_otp}` : undefined, duration: 8000 });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const verifyAndLoad = async () => {
    setBusy(true);
    try {
      const { data: v } = await axios.post(`${API}/public/otp/verify`, { phone, otp: otp.code });
      const { data } = await axios.post(`${API}/public/fees/summary`, { school_code: code, admission_no: adm, phone, verify_token: v.verify_token });
      setOtp({ ...otp, token: v.verify_token });
      setSummary(data);
      setAmount(data.due ? String(data.due) : "");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/fees/pay`, { school_code: code, admission_no: adm, phone, verify_token: otp.token, amount: Number(amount), return_base: window.location.origin });
      window.location.assign(data.redirectUrl);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="pub-mesh min-h-[80vh]">
      <div className="mx-auto max-w-3xl px-5 py-12" data-testid="pay-fees-page">
        <Link to={`/s/${code}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-700"><ArrowLeft size={15} /> {s?.name || "Back"}</Link>
        <h1 className="mt-3 text-3xl font-bold font-head sm:text-4xl">Pay School Fees Online</h1>
        <p className="mt-2 text-slate-600">Secure payment via PhonePe · instant receipt on WhatsApp</p>

        {!summary ? (
          <div className="pub-card mt-8 p-6 sm:p-8">
            <div className="grid gap-4 sm:grid-cols-2">
              <PubField label="Admission Number *"><input className="pub-input font-mono uppercase" placeholder="SRUPS-1002" value={adm} onChange={(e) => setAdm(e.target.value.toUpperCase())} data-testid="pf-admission-no" /></PubField>
              <PubField label="Registered Mobile *"><input className="pub-input" placeholder="10-digit mobile" maxLength={10} value={phone} onChange={(e) => { setPhone(e.target.value); setOtp({ sent: false, code: "", mock: "", token: "" }); }} data-testid="pf-phone" /></PubField>
            </div>
            <div className="mt-5 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-indigo-800"><ShieldCheck size={16} /> Verify mobile to view dues</div>
              {!otp.sent ? (
                <button className="pub-btn mt-3" onClick={sendOtp} disabled={busy || phone.length < 10 || !adm} data-testid="pf-send-otp">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Send OTP</button>
              ) : (
                <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                  <input className="pub-input font-mono tracking-[0.4em]" placeholder="••••••" maxLength={6} value={otp.code} onChange={(e) => setOtp({ ...otp, code: e.target.value.replace(/\D/g, "") })} data-testid="pf-otp" />
                  <button className="pub-btn shrink-0 !bg-indigo-600" onClick={verifyAndLoad} disabled={busy || otp.code.length !== 6} data-testid="pf-verify">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Verify & View Dues</button>
                </div>
              )}
              {otp.sent && otp.mock && <p className="mt-2 text-[11px] text-slate-500">SMS gateway not connected yet (MOCK) — OTP: <b className="font-mono">{otp.mock}</b></p>}
            </div>
          </div>
        ) : (
          <div className="mt-8 grid gap-5 lg:grid-cols-5" data-testid="pf-summary">
            <div className="pub-card p-6 lg:col-span-3">
              <div className="flex items-center gap-4">
                {summary.student.photo_url ? <img src={fileUrl(summary.student.photo_url)} alt="" className="h-16 w-16 rounded-xl object-cover ring-1 ring-slate-200" /> : <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-indigo-100 text-lg font-bold text-indigo-700">{summary.student.name.slice(0, 2)}</div>}
                <div>
                  <h2 className="text-xl font-semibold font-head" data-testid="pf-student-name">{summary.student.name}</h2>
                  <p className="text-sm text-slate-500">Class {summary.student.class_name}-{summary.student.section} · {summary.student.admission_no}</p>
                  <p className="text-xs text-slate-500">Father: {summary.student.father_name || summary.student.parent_name}</p>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                {[["Total Fee", summary.student.total_fee, "text-slate-800"], ["Paid", summary.student.paid_fee, "text-emerald-600"], ["Pending", summary.due, "text-rose-600"]].map(([l, v, c]) => (
                  <div key={l} className="rounded-xl bg-slate-50 p-3"><p className="text-[11px] uppercase tracking-wider text-slate-500">{l}</p><p className={`text-lg font-bold font-head ${c}`} data-testid={`pf-${l.toLowerCase().replace(" ", "-")}`}>{inr(v)}</p></div>
                ))}
              </div>
              <h3 className="mt-6 flex items-center gap-2 text-sm font-semibold"><Receipt size={15} /> Payment history</h3>
              <ul className="mt-2 divide-y divide-slate-100 text-sm">
                {summary.receipts.map((r) => <li key={r.id} className="flex items-center justify-between py-2"><span><span className="font-mono text-xs text-slate-500">{r.receipt_no}</span> · {r.heads.map((h) => h.name).join(", ")}</span><span className="font-semibold text-emerald-600">{inr(r.total)}</span></li>)}
                {!summary.receipts.length && <li className="py-2 text-slate-500">No payments yet</li>}
              </ul>
            </div>
            <div className="pub-card p-6 lg:col-span-2">
              <h3 className="font-semibold font-head text-lg">Pay now</h3>
              <PubField label="Amount (₹)" hint={summary.due ? `Pending dues ${inr(summary.due)}` : "No pending dues — you may still pay in advance"}>
                <input type="number" min="1" className="pub-input text-lg font-semibold" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pf-amount" />
              </PubField>
              <div className="mt-3 flex flex-wrap gap-2">
                {[summary.due, 5000, 2500, 1000].filter((v, i, a) => v > 0 && a.indexOf(v) === i).map((v) => <button key={v} onClick={() => setAmount(String(v))} className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold hover:border-indigo-400 hover:text-indigo-700" data-testid={`pf-quick-${v}`}>{inr(v)}</button>)}
              </div>
              <button className="pub-btn mt-5 w-full !bg-[#5F259F] hover:!bg-[#4a1d7d]" onClick={pay} disabled={busy || !(Number(amount) >= 1)} data-testid="pf-pay-btn">{busy ? <Loader2 size={15} className="animate-spin" /> : <Smartphone size={15} />} Pay {amount ? inr(Number(amount)) : ""} with PhonePe</button>
              <p className="mt-3 text-center text-[11px] text-slate-500">{summary.gateway === "phonepe" ? "You will be redirected to PhonePe secure checkout." : "PhonePe not yet connected for this school — a simulated checkout (MOCK) will open."}</p>
              <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-slate-400"><IndianRupee size={11} /> Receipt auto-generated & sent on WhatsApp</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
